import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import type { ProjectMemberRow } from '../types'

const CHIP_CLASS =
  'inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs text-ink-soft'

/**
 * A project roster row as a linked chip — initial avatar, name, role and side
 * (firm/client), no allocation percentage (Agon's ruling: there is no
 * staffing/allocation backend). Distinct from `components/px/PersonChip`,
 * which is a demo-content component (`@/demo/data`'s `Person`) a live page
 * must not import (`demoBoundary.test.ts`).
 *
 * Links to the member's person profile by user id when one is present; a
 * legacy roster row with no `userId` renders as a plain, unlinked chip
 * (AC-10).
 */
export function MemberChip({ member }: { member: ProjectMemberRow }) {
  const { t } = useTranslation('portfolio')
  const name = member.name ?? member.id ?? t('admin.unnamedMember')
  const initial = member.initials ?? name.charAt(0).toUpperCase()
  const sideLabel = t(member.side === 'CLIENT' ? 'admin.side.CLIENT' : 'admin.side.FIRM')

  // One chip body for both shapes — only the wrapper differs (link vs plain).
  const body = (
    <>
      <span
        aria-hidden
        className="flex h-4 w-4 items-center justify-center rounded-full bg-people-bg text-[0.5625rem] font-semibold text-people-ink"
      >
        {initial}
      </span>
      <span>{name}</span>
      {member.role !== undefined && member.role !== '' && <span>{member.role}</span>}
      <span className="text-muted-foreground">{sideLabel}</span>
    </>
  )

  if (member.userId === undefined) {
    return (
      <span data-testid="member-chip" className={CHIP_CLASS}>
        {body}
      </span>
    )
  }

  return (
    <Link
      data-testid="member-chip"
      to={`/resources/people/${encodeURIComponent(member.userId)}`}
      className={cn(CHIP_CLASS, 'hover:bg-muted')}
    >
      {body}
    </Link>
  )
}
